import { useState } from "react";
import { WarningOctagon, ArrowRight, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

const PAST_DUE_STATUSES = new Set(["past_due", "unpaid", "incomplete"]);

function statusCopy(status) {
  switch (status) {
    case "past_due":
      return {
        label: "Payment past due",
        body: "Your last invoice failed to charge. Update your card now to keep access — your data is still safe.",
      };
    case "unpaid":
      return {
        label: "Subscription unpaid",
        body: "Multiple payment attempts have failed. Update your card immediately to restore access.",
      };
    case "incomplete":
      return {
        label: "Payment incomplete",
        body: "Your initial payment didn't go through. Finish payment to activate your account.",
      };
    default:
      return { label: "Billing issue", body: "There's an issue with your subscription." };
  }
}

export default function PastDueBanner() {
  const { user } = useAuth();
  const [redirecting, setRedirecting] = useState(false);

  const plan = user?.plan;
  if (!plan || plan.is_demo) return null;
  if (!PAST_DUE_STATUSES.has(plan.subscription_status)) return null;

  const copy = statusCopy(plan.subscription_status);

  const onUpdateCard = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(msg);
      setRedirecting(false);
    }
  };

  return (
    <div
      data-testid="past-due-banner"
      role="alert"
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b-2 bg-[#FEE2E2] border-[#DC2626] text-[#7F1D1D]"
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <WarningOctagon
          size={22}
          weight="fill"
          className="text-[#DC2626] flex-shrink-0 mt-0.5"
        />
        <div className="min-w-0">
          <div className="font-bold uppercase tracking-widest text-[10px] sm:text-[11px]">
            {copy.label}
          </div>
          <div className="text-xs sm:text-sm leading-snug mt-0.5">
            {copy.body}
          </div>
        </div>
      </div>
      <button
        onClick={onUpdateCard}
        disabled={redirecting}
        data-testid="past-due-banner-cta"
        className="inline-flex items-center justify-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-[10px] sm:text-[11px] uppercase tracking-widest px-4 py-2.5 transition-colors flex-shrink-0 w-fit"
      >
        <CreditCard size={13} weight="bold" />
        {redirecting ? "Redirecting…" : "Update card"}
        {!redirecting && <ArrowRight size={11} weight="bold" />}
      </button>
    </div>
  );
}
